import { ApiClient } from '@center-technologies/shared-api-clients';
import { Sphere } from 'types/global';
import { invalidateSphereById } from './getSphereById';

export interface PatchSpherePropertiesParams {
  sphereId: string;
  properties: Record<string, any>;
}

export interface PatchSpherePropertiesResponse {
  sphere: Sphere;
}

export class PatchSpherePropertiesError extends Error {
  details: string | string[];
  /** HTTP status of the failed response, when the failure came from the server. */
  status?: number;
  /** The server's stable denial code, when it sent one (e.g. a write-tier `aspect_read_only` 403). */
  reason?: string;
  /**
   * The server's own `error` code, only when the response body carried one (e.g. 503
   * `permission_service_unreachable`). Unlike `details`, never a transport message.
   */
  serverCode?: string;

  constructor(message: string, details: string | string[], status?: number, reason?: string, serverCode?: string) {
    super(message);
    this.details = details;
    this.status = status;
    this.reason = reason;
    this.serverCode = serverCode;
    this.name = 'PatchSpherePropertiesError';
  }
}

/**
 * Patches the properties of an existing sphere in the Graph system.
 *
 * @param apiClient - An ApiClient configured for your Graph endpoint
 * @param params.sphereId - The ID of the Sphere to patch
 * @param params.properties - Key-value pairs of properties to be updated
 * @returns A promise that resolves to the updated Sphere
 * @throws PatchSpherePropertiesError on failure
 */
export async function patchSphereProperties(
  apiClient: ApiClient,
  params: PatchSpherePropertiesParams
): Promise<Sphere> {
  const { sphereId, properties } = params;
  
  if (!sphereId?.trim() || !properties) {
    throw new PatchSpherePropertiesError(
      'Invalid parameters',
      'sphereId and properties are required'
    );
  }

  try {
    // ApiClient.patch automatically handles the Bearer token header
    const data = await apiClient.patch<PatchSpherePropertiesResponse>(
      `/spheres/${sphereId}/properties`,
      properties
    );

    const patchedSphere: Sphere = data.sphere;

    // Keep the full-sphere read cache from serving the pre-patch vertex.
    invalidateSphereById(sphereId);

    console.info(`Patched sphere with ID ${patchedSphere.id}.`);

    return patchedSphere;
  } catch (err: any) {
    const msg =
      err.response?.data?.error ??
      err.message ??
      `Unknown error patching sphere ${sphereId}`;
    
    console.error(`Error patching sphere with ID ${sphereId}:`, msg);
    
    throw new PatchSpherePropertiesError(
      'Failed to patch sphere properties.',
      msg,
      err.response?.status ?? err.status,
      typeof err.response?.data?.reason === 'string' ? err.response.data.reason : undefined,
      typeof err.response?.data?.error === 'string' ? err.response.data.error : undefined
    );
  }
}